import { upsert, CrudError } from '@/lib/crud';
import { syncNow } from '@/lib/sync';
import { newId } from '@/lib/ids';
import type { JobCard, JobCardLabour, JobCardPart, JobKind, JobPriority, JobStatus } from '@/types/models';

/**
 * The central workshop (migration 0037). Job cards are written online,
 * from the workshop, straight to the server (like breakdowns and
 * services); the server's triggers keep the breakdown, the service record,
 * the stock and the vehicle's meter in step, so every save is followed by
 * a sync to bring those back into the mirror.
 */

export const WORKSHOP_ROLES = ['maintenance', 'manager', 'admin'] as const;

export const STATUS_LABEL: Record<JobStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  awaiting_parts: 'Awaiting parts',
  completed: 'Completed',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

export const STATUS_TONE: Record<JobStatus, 'grey' | 'blue' | 'amber' | 'green' | 'red'> = {
  open: 'grey',
  in_progress: 'blue',
  awaiting_parts: 'amber',
  completed: 'green',
  closed: 'grey',
  cancelled: 'grey',
};

export const KIND_LABEL: Record<JobKind, string> = {
  breakdown: 'Breakdown repair',
  service: 'Service',
  defect: 'Check defect',
  inspection: 'Inspection',
  other: 'Other work',
};

export const PRIORITY_LABEL: Record<JobPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

export const PRIORITY_TONE: Record<JobPriority, 'grey' | 'blue' | 'amber' | 'red'> = {
  low: 'grey',
  normal: 'blue',
  high: 'amber',
  urgent: 'red',
};

/** The board's columns, left to right. */
export const BOARD_COLUMNS: JobStatus[] = ['open', 'in_progress', 'awaiting_parts', 'completed'];

export const LIVE_STATUSES: JobStatus[] = ['open', 'in_progress', 'awaiting_parts'];

/** Where a card may go next — the server's rule, shown as buttons. */
export function nextStatuses(status: JobStatus, lead: boolean, kind?: JobKind): JobStatus[] {
  switch (status) {
    case 'open': return ['in_progress', 'awaiting_parts', 'completed', 'cancelled'];
    case 'in_progress': return ['awaiting_parts', 'completed', 'cancelled'];
    case 'awaiting_parts': return ['in_progress', 'completed', 'cancelled'];
    // A completed service card has recorded the service: it is corrected,
    // not reopened.
    case 'completed': return kind === 'service' ? ['closed'] : ['closed', 'in_progress'];
    case 'closed': return lead ? ['completed'] : [];
    case 'cancelled': return lead ? ['open'] : [];
  }
}

/** What the button that moves a card to `to` says. */
export function actionLabel(from: JobStatus, to: JobStatus): string {
  if (to === 'in_progress') return from === 'completed' ? 'Reopen — not fixed' : from === 'awaiting_parts' ? 'Parts in — resume' : 'Start work';
  if (to === 'awaiting_parts') return 'Awaiting parts';
  if (to === 'completed') return from === 'closed' ? 'Reopen' : 'Complete';
  if (to === 'closed') return 'Close card';
  if (to === 'cancelled') return 'Cancel card';
  if (to === 'open') return 'Reopen';
  return STATUS_LABEL[to];
}

/** Breakdown severity → a card's priority. */
export function priorityFor(severity: string | null | undefined): JobPriority {
  return severity === 'critical' ? 'urgent' : severity === 'major' ? 'high' : 'normal';
}

export function isLate(card: Pick<JobCard, 'due_date' | 'status'>, today: string): boolean {
  return !!card.due_date && card.due_date < today && LIVE_STATUSES.includes(card.status);
}

export interface CardCost {
  hours: number;
  labour: number;
  store: number;
  outside: number;
  total: number;
}

/** Costs from the mirror (the workshop's own rows). */
export function costOf(labour: JobCardLabour[], parts: JobCardPart[]): CardCost {
  const live = <T extends { deleted_at?: string | null }>(r: T) => !r.deleted_at;
  const l = labour.filter(live);
  const p = parts.filter(live);
  const hours = l.reduce((a, x) => a + Number(x.hours), 0);
  const labourCost = l.reduce((a, x) => a + Number(x.cost ?? Number(x.hours) * Number(x.rate)), 0);
  const store = p.filter((x) => x.source === 'store').reduce((a, x) => a + Number(x.total_cost ?? x.qty * x.unit_cost), 0);
  const outside = p.filter((x) => x.source === 'outside').reduce((a, x) => a + Number(x.total_cost ?? x.qty * x.unit_cost), 0);
  return { hours, labour: labourCost, store, outside, total: labourCost + store + outside };
}

/** Save a card; then pull what its triggers changed elsewhere. */
export async function saveJobCard(card: Partial<JobCard> & { machine_id: string; title: string }): Promise<JobCard> {
  const saved = await upsert('job_cards', { ...card, id: card.id ?? newId() } as Record<string, unknown>);
  void syncNow();
  return saved as unknown as JobCard;
}

/** Open a card for a breakdown, or return the live one it already has. */
export async function cardForBreakdown(
  existing: JobCard | undefined,
  bd: { id: string; machine_id: string; failure_description?: string | null; severity?: string | null;
        hour_meter_at_failure?: number | null },
): Promise<JobCard> {
  if (existing) return existing;
  return saveJobCard({
    machine_id: bd.machine_id,
    kind: 'breakdown',
    breakdown_id: bd.id,
    title: (bd.failure_description ?? '').trim() || 'Breakdown repair',
    priority: priorityFor(bd.severity),
    meter_reading: bd.hour_meter_at_failure ?? null,
    status: 'open',
  });
}

export { CrudError };
